import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark } from "lucide-react";
import {
  useSavedThreads,
  useUnsaveThread,
} from "../../../services/communities";
import ThreadCard from "../threads/ThreadCard";
import "./SavedThreadsView.css";

/**
 * View for browsing saved threads - simplified single column layout
 */
const SavedThreadsView = ({ onThreadClick }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data: savedData, isLoading: threadsLoading } = useSavedThreads(
    null, // No collection filter
    page,
    20
  );
  const unsaveThreadMutation = useUnsaveThread();

  // Handle thread click - use prop if provided, otherwise navigate internally
  const handleThreadClick = useCallback(
    (thread) => {
      if (onThreadClick) {
        onThreadClick(thread);
      } else {
        // Navigate to thread detail page
        const communityId = thread.community?._id || thread.community;
        navigate(`/communities/${communityId}/threads/${thread._id}`);
      }
    },
    [onThreadClick, navigate]
  );

  // Handle community click - navigate to community page
  const handleCommunityClick = useCallback(
    (community) => {
      navigate(`/communities/${community._id}`);
    },
    [navigate]
  );

  // Ensure all saved threads have isSaved: true since they're in the saved list
  const threads = (savedData?.threads || []).map((savedItem) => ({
    // The saved item may have thread nested or be the thread itself
    ...(savedItem.thread || savedItem),
    isSaved: true, // All threads in saved view are saved by definition
    savedAt: savedItem.savedAt,
    collectionName: savedItem.collectionName,
    notes: savedItem.notes,
    community: savedItem.community || savedItem.thread?.community,
  }));
  const pagination = savedData?.pagination || {};

  const handleUnsave = async (threadId) => {
    try {
      await unsaveThreadMutation.mutateAsync({ threadId });
    } catch (error) { /* ignored */ }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString();
  };

  // Loading state
  if (threadsLoading) {
    return (
      <div className="saved-threads-view">
        <div className="saved-threads-loading">
          <div className="spinner"></div>
          <p>Loading saved threads...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (threads.length === 0) {
    return (
      <div className="saved-threads-view">
        <div className="saved-threads-empty">
          <Bookmark size={48} />
          <h3>No saved threads yet</h3>
          <p>Save threads to read them later</p>
        </div>
      </div>
    );
  }

  // Threads list
  return (
    <div className="saved-threads-view">
      <div className="saved-threads-list">
        {threads.map((thread) => (
          <ThreadCard
            key={thread._id}
            thread={thread}
            onThreadClick={handleThreadClick}
            onCommunityClick={handleCommunityClick}
            showCommunityInfo={true}
            formatTimeAgo={formatTimeAgo}
            onSave={() => handleUnsave(thread._id)}
          />
        ))}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="saved-threads-pagination">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {page} of {pagination.totalPages}
          </span>
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SavedThreadsView;



